// Shared explorer parts: the inline name editor (new file + rename) and the
// custom right-click context menu. Both styled to match the command palette
// (popover surface, accent-on-hover rows) so the dock reads as one piece.

import {
  useLayoutEffect,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { FileGlyph, glyphClass, type FileKind } from "./icons";

// --------------------------------------------------------------------------
// Inline edit row (new file + rename)
// --------------------------------------------------------------------------

interface InlineEditProps {
  kind: FileKind;
  initial: string;
  /** Indent one level (a nested args companion is created/renamed inline). */
  depth: number;
  validate: (value: string) => string | null;
  onCommit: (value: string) => void;
  onCancel: () => void;
  /** Select only the basename (before the first dot) on focus, nicer for rename. */
  selectBase?: boolean;
}

export function InlineEdit(props: InlineEditProps): JSX.Element {
  const { kind, initial, depth, validate, onCommit, onCancel, selectBase } = props;
  const [value, setValue] = useState(initial);
  const inputRef = useRef<HTMLInputElement>(null);
  const error = validate(value);
  // Only surface an error once the user has typed something distinct.
  const showError = Boolean(error) && value.trim() !== "" && value.trim() !== initial;

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    if (selectBase) {
      const dot = initial.indexOf(".");
      el.setSelectionRange(0, dot > 0 ? dot : initial.length);
    } else {
      el.select();
    }
  }, [initial, selectBase]);

  const commit = (): void => {
    if (validate(value)) return; // blocked by validation
    onCommit(value.trim());
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div className="exp-edit-host">
      <div className="exp-edit-row" style={{ paddingLeft: rowPad(depth) }}>
        <span className={glyphClass(kind)} aria-hidden="true">
          <FileGlyph kind={kind} />
        </span>
        <input
          ref={inputRef}
          className={`exp-edit-input${showError ? " is-invalid" : ""}`}
          value={value}
          spellCheck={false}
          autoComplete="off"
          aria-label="File name"
          aria-invalid={Boolean(error)}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={commit}
        />
      </div>
      {showError && (
        <div className="exp-edit-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

/** Left padding for a row at a given tree depth (matches .exp-row in the CSS). */
export function rowPad(depth: number): number {
  return 10 + depth * 16;
}

// --------------------------------------------------------------------------
// Context menu
// --------------------------------------------------------------------------

export interface MenuAction {
  id: string;
  label: string;
  icon: ReactNode;
  shortcut?: string;
  danger?: boolean;
  /** A hairline separator is drawn above this item. */
  separated?: boolean;
  run: () => void;
}

interface ContextMenuProps {
  x: number;
  y: number;
  actions: MenuAction[];
  onClose: () => void;
}

// --------------------------------------------------------------------------
// Confirmation dialog (delete)
// --------------------------------------------------------------------------

interface ConfirmDialogProps {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog(props: ConfirmDialogProps): JSX.Element {
  const { title, message, confirmLabel = "Delete", danger, onConfirm, onCancel } = props;
  const confirmRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    confirmRef.current?.focus();
  }, []);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div
      className="cd-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="cd-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="cd-title"
        onKeyDown={onKeyDown}
      >
        <h2 className="cd-title" id="cd-title">
          {title}
        </h2>
        <p className="cd-message">{message}</p>
        <div className="cd-actions">
          <button type="button" className="cd-btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={`cd-btn cd-btn--confirm${danger ? " is-danger" : ""}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ContextMenu(props: ContextMenuProps): JSX.Element {
  const { x, y, actions, onClose } = props;
  const menuRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(0);
  const [pos, setPos] = useState({ left: x, top: y });

  // Keep the menu fully on-screen, then take focus for keyboard nav.
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let left = x;
    let top = y;
    if (left + rect.width > window.innerWidth - 6) left = window.innerWidth - rect.width - 6;
    if (top + rect.height > window.innerHeight - 6) top = window.innerHeight - rect.height - 6;
    setPos({ left: Math.max(6, left), top: Math.max(6, top) });
    el.focus();
  }, [x, y]);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocused((i) => (i + 1) % actions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocused((i) => (i - 1 + actions.length) % actions.length);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      actions[focused]?.run();
    } else if (e.key === "Escape" || e.key === "Tab") {
      e.preventDefault();
      onClose();
    } else if (e.key === "Home") {
      e.preventDefault();
      setFocused(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setFocused(actions.length - 1);
    }
  };

  return (
    <>
      <div
        className="exp-menu-scrim"
        onMouseDown={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      <div
        ref={menuRef}
        className="exp-menu"
        role="menu"
        aria-label="File actions"
        tabIndex={-1}
        style={{ left: pos.left, top: pos.top }}
        onKeyDown={onKeyDown}
      >
        {actions.map((a, i) => (
          <div key={a.id} role="presentation">
            {a.separated && <div className="exp-menu-sep" role="separator" />}
            <button
              type="button"
              role="menuitem"
              className={`exp-menu-item${a.danger ? " is-danger" : ""}${
                i === focused ? " is-focused" : ""
              }`}
              onMouseEnter={() => setFocused(i)}
              onClick={a.run}
            >
              <span className="exp-menu-item-icon" aria-hidden="true">
                {a.icon}
              </span>
              <span className="exp-menu-item-label">{a.label}</span>
              {a.shortcut && <span className="exp-menu-item-key">{a.shortcut}</span>}
            </button>
          </div>
        ))}
      </div>
    </>
  );
}
